// Batch 0 (UX/IA overhaul) — the SINGLE canonical status → visual-tone map.
//
// Before this, every list page redefined its own `statusVariant` record (e.g.
// CameraListPage.tsx), so "offline" was grey on one screen and amber on
// another. This maps any domain status string (camera / NVR / agent / stream /
// health) onto one of the Badge tones, so <StatusBadge> renders consistently
// app-wide. Unknown values fall back to a neutral tone and never throw — a new
// backend status can't crash or miscolour the UI.

export type StatusTone =
  | "online"
  | "offline"
  | "warning"
  | "error"
  | "neutral";

// Raw status VALUE (lowercased) → tone. Covers camera_status, NVR/stream
// status, agent connection state, and health verdicts seen across the API.
const TONE_BY_STATUS: Record<string, StatusTone> = {
  // healthy / connected
  online: "online",
  connected: "online",
  active: "online",
  live: "online",
  ok: "online",
  healthy: "online",
  in_sync: "online",
  // down / inactive (known, not an error)
  offline: "offline",
  disconnected: "offline",
  inactive: "offline",
  stopped: "offline",
  unknown: "neutral",
  never: "neutral",
  // transitional / attention
  pending: "warning",
  unprovisioned: "warning",
  provisional: "warning",
  degraded: "warning",
  warning: "warning",
  stale: "warning",
  drift: "warning",
  suspended: "warning",
  // failure
  error: "error",
  failed: "error",
  critical: "error",
  fault: "error",
};

/** Domain status VALUE → Badge tone. Case-insensitive; unknown → "neutral". */
export function statusTone(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  return TONE_BY_STATUS[status.toLowerCase()] ?? "neutral";
}
